import React, { useRef, useEffect, useLayoutEffect, useMemo, useState, useCallback } from 'react';
import { useSearchParams } from 'next/navigation';
import Skeleton from 'react-loading-skeleton';
import { BrandstakOrder } from 'shared/types/cart/Order';
import Message from 'components/commercetools-ui/atoms/message';
import Button from 'components/commercetools-ui/atoms/button';
import Link from 'components/commercetools-ui/atoms/link';
import Slider from 'components/commercetools-ui/atoms/slider';
import Typography from 'components/commercetools-ui/atoms/typography';
import OrderSummary from 'components/commercetools-ui/organisms/order-summary';
import Wrapper from 'components/HOC/wrapper';
import useClassNames from 'helpers/hooks/useClassNames';
import { useFormat } from 'helpers/hooks/useFormat';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { useAccount } from 'frontastic';
import { ImageProps } from 'frontastic/lib/image';
import OrderSkeleton from './components/order-skeleton';
import ShipmentDetails from './components/shipment-details';
import useOrderData from './helper-hooks/useOrderData';
import useOrderFetch from './helper-hooks/useOrderFetch';
import OrderItem from './OrderItem';

export interface StatusTab {
  name: string;
  slug: string;
}

interface OrdersProps {
  productLogo?: ImageProps;
}

const Orders = ({ productLogo }: OrdersProps) => {
  const [isOldOrder, setIsOldOrder] = useState(false);
  const { orders, getOrder, loading, error, initialLoadCompleted, loadingOldOrders } = useOrderFetch(isOldOrder);
  const { hasOldOrders } = useAccount();
  const params = useSearchParams();
  const ref = useRef<HTMLDivElement>(null);
  const [isLargeMobileScreen] = useMediaQuery(325);
  const { formatMessage: formatOrdersMessage } = useFormat({ name: 'orders' });
  const [productDetails, setProductDetails] = useState<BrandstakOrder | undefined>();
  const [hasAccountHaveOldOrders, setHasAccountHaveOldOlders] = useState(false);
  const [overflow, setOverflow] = useState<boolean | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckDone, setIsCheckDone] = useState(false);
  const [orderFetchError, setOrderFetchError] = useState(false);

  const {
    formattedOrderDate,
    orderDetailsShippingInfo,
    paymentInfo,
    trackingURLs,
    orderInfoLoaded,
    isCommerceToolsOrder,
  } = useOrderData(productDetails);

  const loadingInProgress = useMemo(() => {
    return loading || (orders.length == 0 && (!isCheckDone || loadingOldOrders));
  }, [loading, orders, isCheckDone, loadingOldOrders]);

  useEffect(() => {
    const getOldOrders = async () => {
      try {
        const res = await hasOldOrders();
        if (res && orders && orders.length == 0) {
          handleShowAllOrders();
          setHasAccountHaveOldOlders(res);
          setIsCheckDone(true);
        } else {
          setIsCheckDone(true);
          setHasAccountHaveOldOlders(res);
        }
      } catch (e) {
        console.error('Error while checking older history');
      }
    };
    if (initialLoadCompleted && !isCheckDone) {
      getOldOrders();
    }
  }, [initialLoadCompleted, isCheckDone]);

  const handleShowAllOrders = () => {
    setIsLoading(true);

    const timeoutId = setTimeout(() => {
      setIsOldOrder(true);
      setHasAccountHaveOldOlders(false);
      setIsLoading(false);
    }, 10);

    return () => clearTimeout(timeoutId);
  };

  const statusTabs: StatusTab[] = [
    { name: formatOrdersMessage({ id: 'all.orders', defaultMessage: 'All orders' }), slug: 'allOrders' },
    // { name: formatOrdersMessage({ id: 'Confirmed', defaultMessage: 'Registered' }), slug: 'confirmed' },
    // { name: formatOrdersMessage({ id: 'Complete', defaultMessage: 'Delivered' }), slug: 'complete' },
    // { name: formatOrdersMessage({ id: 'Cancelled', defaultMessage: 'Cancelled' }), slug: 'cancelled' },
  ];
  const [selectedTab, setSelectedTab] = useState(statusTabs[0].slug);
  const [leftArrowAppear, setLeftArrowAppear] = useState<boolean | undefined>(undefined);
  const [rightArrowAppear, setRightArrowAppear] = useState<boolean | undefined>(undefined);
  useEffect(() => {
    if (overflow || !isLargeMobileScreen) {
      setRightArrowAppear(true);
    }
  }, [leftArrowAppear, rightArrowAppear, isLargeMobileScreen, overflow]);

  const orderHistoryContent = useMemo(() => {
    if (selectedTab === 'allOrders') return orders;
    else return orders?.filter((order: BrandstakOrder) => order.orderState === selectedTab);
  }, [selectedTab, orders]);

  const tabTextClassNames = (tab: StatusTab) => {
    return `border-primary-black pb-8 ${
      tab.slug === selectedTab ? 'border-b-2 text-primary-black font-medium' : 'text-secondary-black'
    }`;
  };

  const mobileStatusWrapper = useClassNames([
    'h-fit w-full',
    leftArrowAppear === true ? 'pl-36' : 'pl-16',
    rightArrowAppear === true ? 'pr-36' : 'pr-16',
    !overflow && 'flex justify-center px-16',
  ]);

  const swiperReachBeginning = useCallback(() => {
    !overflow ? setRightArrowAppear(false) : setRightArrowAppear(true);
    setLeftArrowAppear(false);
  }, [overflow]);

  const swiperReachEnd = useCallback(() => {
    !overflow ? setLeftArrowAppear(false) : setLeftArrowAppear(true);
    setRightArrowAppear(false);
  }, [overflow]);

  useEffect(() => {
    setOrderFetchError(false);
    if (params.get('id')) {
      getOrder(params.get('id') as string, params.get('orderSource') as string)
        .then((res) => {
          setProductDetails(res);
        })
        .catch((error) => {
          setOrderFetchError(true);
          setProductDetails(undefined);
        });
    } else {
      setProductDetails(undefined);
    }
  }, [getOrder, params]);

  useLayoutEffect(() => {
    if (ref?.current && ref.current.clientWidth < ref?.current.scrollWidth) setOverflow(true);
  }, [ref]);

  return (
    <>
      {loadingInProgress ? (
        <OrderSkeleton count={2} />
      ) : (
        <>
          <Typography
            as="h2"
            className="mt-20 hidden text-22 text-primary-black md:ml-24 md:block lg:ml-44 lg:mt-42 lg:text-24"
          >
            {formatOrdersMessage({
              id: params.get('id') ? 'orders.id' : 'orders',
              defaultMessage: params.get('id') ? 'Order Details' : 'Orders',
            })}
          </Typography>

          {params.get('id') ? (
            <div className="mt-20 px-16 md:mt-36 md:px-24 lg:px-44">
              {orderFetchError ? (
                <>
                  <div>
                    <Typography className="inline text-14 text-secondary-black md:text-16">
                      {formatOrdersMessage({
                        id: 'order.number',
                        defaultMessage: `Order number: `,
                      })}
                    </Typography>
                    <Typography className="inline text-14 text-black md:text-16">
                      {formatOrdersMessage({
                        id: 'order.id.tmp',
                        defaultMessage: `${params.get('id')?.replaceAll('-', ' ')}`,
                      })}
                    </Typography>
                  </div>
                  <div className="mt-10">
                    <Message
                      severity="error"
                      message={formatOrdersMessage({
                        id: 'no.order.details.found',
                        defaultMessage: 'The order details could not be loaded at this time',
                      })}
                    />
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <Typography className="inline text-14 text-secondary-black md:text-16">
                      {formatOrdersMessage({
                        id: 'order.number',
                        defaultMessage: `Order number: `,
                      })}
                    </Typography>
                    <Typography className="inline text-14 text-black md:text-16">
                      {formatOrdersMessage({
                        id: 'order.id.tmp',
                        defaultMessage: `${params.get('id')?.replaceAll('-', ' ')}`,
                      })}
                    </Typography>
                  </div>
                  <div className="mt-10">
                    <Typography className="inline text-14 text-secondary-black md:text-16">
                      {formatOrdersMessage({
                        id: 'date.ordered',
                        defaultMessage: `Order Date: `,
                      })}
                    </Typography>
                    <Typography className="inline text-14 text-black md:text-16">{formattedOrderDate}</Typography>
                    {!formattedOrderDate && <Skeleton className="h-20 w-110"></Skeleton>}
                  </div>
                  <div className="mt-20 flex flex-col md:mt-36 lg:flex-row lg:items-start lg:gap-26">
                    <div className="border-neutral-400 lg:w-[65%] lg:border lg:p-16">
                      <div className="flex flex-wrap gap-5 md:gap-0">
                        <div className="md:w-156">
                          <Typography className="mb-8 text-14 text-secondary-black" asSkeleton={loading}>
                            {formatOrdersMessage({
                              id: 'shipping.method',
                              defaultMessage: 'Shipping method: ',
                            })}
                          </Typography>
                        </div>
                        <Typography className="text-14 text-black" asSkeleton={loading || !orderInfoLoaded}>
                          {formatOrdersMessage({
                            id: 'shipping.description',
                            defaultMessage: orderDetailsShippingInfo,
                          })}
                        </Typography>
                      </div>
                      <div className="mt-16 flex flex-wrap gap-5 md:gap-0">
                        <div className="md:w-156">
                          <Typography className="mb-8 text-14 text-secondary-black" asSkeleton={loading}>
                            {formatOrdersMessage({
                              id: 'shipping.address',
                              defaultMessage: 'Shipping address: ',
                            })}
                          </Typography>
                        </div>
                        {orderInfoLoaded && (
                          <p className="max-w-full overflow-hidden truncate text-14 text-black">
                            {`${productDetails?.shippingAddress?.firstName} ${productDetails?.shippingAddress?.lastName}`.toUpperCase()}
                            <span className="mt-8 block" />
                            {`${productDetails?.shippingAddress?.streetName}, ${productDetails?.shippingAddress?.city}, ${productDetails?.shippingAddress?.postalCode}`.toUpperCase()}
                          </p>
                        )}
                        {!orderInfoLoaded && <Skeleton className="h-20 w-150"></Skeleton>}
                      </div>

                      {isCommerceToolsOrder && (
                        <div className="mt-16 flex flex-wrap gap-5 md:gap-0">
                          <div className="md:w-156">
                            <Typography className="mb-8 text-14 text-secondary-black" asSkeleton={loading}>
                              {formatOrdersMessage({
                                id: 'payment.method',
                                defaultMessage: 'Payment method: ',
                              })}
                            </Typography>
                          </div>
                          {orderInfoLoaded && (
                            <Typography className="text-14 text-black" asSkeleton={loading}>
                              {paymentInfo?.toUpperCase()}
                            </Typography>
                          )}
                          {!orderInfoLoaded && <Skeleton className="h-20 w-50"></Skeleton>}
                        </div>
                      )}

                      <div className="mt-24">
                        {trackingURLs.length > 0 && <ShipmentDetails order={productDetails} />}
                        {trackingURLs.length > 0 && <div className="mb-30 border-t"></div>}
                        <Typography className="mb-8 text-14 text-black" asSkeleton={loading}>
                          {formatOrdersMessage({
                            id: 'order.question',
                            defaultMessage: 'Do you have questions about your order?',
                          })}
                        </Typography>
                        <Link link="/en/account/?hash=support" className="text-14 text-black">
                          {formatOrdersMessage({
                            id: 'order.support',
                            defaultMessage: 'Contact customer support',
                          })}
                        </Link>
                      </div>
                      {/* TODO: formatting tracking URLs on UI */}
                    </div>
                    <OrderSummary
                      className="border-neutral-400 bg-white px-16 pb-12 pt-24 md:px-24 md:py-12 lg:mt-0 lg:block lg:w-[35%] lg:border lg:p-16"
                      order={productDetails}
                      dataReference="order"
                      includeItemsList
                      productLogo={productLogo}
                      classNames={{
                        infoContainer: 'border-none p-0 pt-16',
                        totalAmount:
                          'md:text-18 font-medium flex items-center justify-between lg:border-t lg:border-neutral-400 lg:pt-12',
                      }}
                    />
                  </div>
                </>
              )}
            </div>
          ) : (
            <>
              {initialLoadCompleted && orders?.length === 0 && (
                <>
                  <div className="mt-20 px-16 md:mt-36 md:px-24 lg:px-44">
                    {error && (
                      <Typography className="inline text-14 text-black md:text-16">
                        {formatOrdersMessage({
                          id: 'error.happened',
                          defaultMessage:
                            "Unfortunately, your order list can't be displayed at this time, please try again later",
                        })}
                      </Typography>
                    )}

                    {!error && isCheckDone && (
                      <Typography className="inline text-14 text-black md:text-16">
                        {formatOrdersMessage({
                          id: 'no.order.found',
                          defaultMessage: 'Unfortunately, we have no order records for this account.',
                        })}
                      </Typography>
                    )}
                  </div>
                </>
              )}
              {initialLoadCompleted && orders?.length > 0 && (
                <>
                  <div className="mt-20 px-16 md:mt-36 md:px-24 lg:px-44">
                    <Typography className="text-14 text-secondary-black md:text-16">
                      {formatOrdersMessage({
                        id: 'help.question',
                        defaultMessage: 'Check status of recent orders, manage your returns and download invoices.',
                      })}
                    </Typography>
                  </div>
                  <div className="mt-16">
                    <Wrapper className="relative h-32 w-full border-b-2 border-neutral-400 md:hidden">
                      <div className={mobileStatusWrapper} ref={ref}>
                        <Slider
                          onReachEnd={swiperReachEnd}
                          onReachBeginning={swiperReachBeginning}
                          slideWidthIsFlexible
                          dots={false}
                          prevButtonStyles={{
                            left: '-20px',
                            transform: 'translateY(-70%) rotateZ(135deg) scale(0.55)',
                            borderWidth: '0 3px 3px 0',
                          }}
                          nextButtonStyles={{
                            right: '-20px',
                            transform: ' translateY(-70%) rotateZ(-45deg) scale(0.55)',
                            borderWidth: '0 3px 3px 0',
                          }}
                          allowTouchMove
                          allowArrowsOnTouchDevice
                          arrows={overflow}
                          spaceBetween={20}
                        >
                          {statusTabs.map((tab) => (
                            <div
                              key={tab.slug}
                              onClick={() => setSelectedTab(tab.slug)}
                              className="w-fit cursor-pointer whitespace-nowrap"
                            >
                              <Typography className={`${tabTextClassNames(tab)} text-14`}>{tab.name}</Typography>
                            </div>
                          ))}
                        </Slider>
                      </div>
                    </Wrapper>
                  </div>

                  <div className="px-16 md:px-24 lg:px-44">
                    <div className="relative hidden h-58 w-full border-b-2 border-neutral-400 pt-24 md:flex">
                      <div className="absolute flex h-fit w-[40%] justify-between">
                        {statusTabs.map((tab) => (
                          <div
                            key={tab.slug}
                            onClick={() => setSelectedTab(tab.slug)}
                            className="cursor-pointer whitespace-nowrap pr-36"
                          >
                            <Typography className={tabTextClassNames(tab)}>{tab.name}</Typography>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="overflow-auto px-16 py-24 md:px-24 lg:px-44">
                    {orderHistoryContent?.map((order?: BrandstakOrder) => (
                      <OrderItem key={order?.orderId} order={order} />
                    ))}
                  </div>

                  {hasAccountHaveOldOrders && isCheckDone && (
                    <div className="px-16 md:px-24 lg:px-44">
                      <Button
                        className="text-16 underline"
                        onClick={handleShowAllOrders}
                        variant="ghost"
                        loading={isLoading}
                      >
                        {'>>Show All Orders<<'}
                      </Button>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </>
      )}
    </>
  );
};

export default Orders;
